import React, { useState } from 'react';
import Navbar from '../../components/Navbar';
import { Search, ChevronDown, ChevronUp, HelpCircle, Book, Shield, DollarSign, User } from 'lucide-react';

const FAQPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');
    const [expandedId, setExpandedId] = useState(null);

    const categories = [
        { id: 'all', name: 'All Questions', icon: <HelpCircle size={18} /> },
        { id: 'general', name: 'General', icon: <Book size={18} /> },
        { id: 'account', name: 'Account & KYC', icon: <User size={18} /> },
        { id: 'security', name: 'Security', icon: <Shield size={18} /> },
        { id: 'financial', name: 'Deeposit & Withdraw', icon: <DollarSign size={18} /> }
    ];

    const faqs = [
        {
            id: 1,
            category: 'general',
            question: 'What is Albatech?',
            answer: 'Albatech is a leading cryptocurrency exchange platform that allows you to trade, buy, and sell various digital assets securely and efficiently.'
        },
        {
            id: 2,
            category: 'account',
            question: 'How do I verify my account (KYC)?',
            answer: 'To verify your account, go to Profile > KYC Verification. You will need to upload a government-issued ID (Passport, ID Card, or Driver\'s License) and a selfie. Verification usually takes less than 24 hours.'
        },
        {
            id: 3,
            category: 'financial',
            question: 'How long do deposits take?',
            answer: 'Crypto deposits depend on the blockchain network confirmations. Usually, it takes 10-30 minutes. Bank transfers may take 1-3 business days depending on your bank.'
        },
        {
            id: 4,
            category: 'security',
            question: 'How do I enable 2FA?',
            answer: 'Go to Profile > Security to enable Two-Factor Authentication (2FA). We recommend using Google Authenticator or Authy App for maximum security.'
        },
        {
            id: 5,
            category: 'financial',
            question: 'What are the withdrawal fees?',
            answer: 'Withdrawal fees vary by coin/token and network conditions. You can check the specific fee on the withdrawal page before confirming your transaction or visit our Fee Schedule page.'
        },
        {
            id: 6,
            category: 'account',
            question: 'Can I change my registered email?',
            answer: 'For security reasons, users cannot change their email address manually. Please contact our support team if you need to update your email.'
        },
        {
            id: 7,
            category: 'general',
            question: 'Is Albatech available in my country?',
            answer: 'Albatech is available globally, except in restricted jurisdictions such as North Korea, Iran, and other sanctioned countries. Please check our Terms of Service for the full list.'
        },
        {
            id: 8,
            category: 'security',
            question: 'What should I do if I suspect unauthorized access?',
            answer: 'Immediately disable your account by contacting support or using the "Disable Account" link in security emails. Change your password and 2FA immediately.'
        }
    ];

    const filteredFaqs = faqs.filter(faq => {
        const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="bg-[#0b0e11] min-h-screen text-gray-100 font-sans selection:bg-blue-500/30">
            <Navbar />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-center mb-12">
                    <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">Frequently Asked Questions</h1>
                    <p className="text-gray-400 max-w-xl mx-auto mb-8">
                        Find answers to common questions about using Albatech.
                    </p>

                    <div className="relative max-w-lg mx-auto">
                        <input
                            type="text"
                            placeholder="Search for answers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-[#1e2329] border border-gray-700 rounded-xl px-5 py-4 pl-12 text-white focus:outline-none focus:border-blue-500 transition shadow-lg"
                        />
                        <Search className="absolute left-4 top-4 text-gray-500" size={20} />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 justify-center mb-10">
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition ${activeCategory === cat.id
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-[#1e2329] text-gray-400 hover:bg-[#2b3139] hover:text-white'
                                }`}
                        >
                            {cat.icon}
                            {cat.name}
                        </button>
                    ))}
                </div>

                <div className="space-y-4">
                    {filteredFaqs.length > 0 ? (
                        filteredFaqs.map(faq => (
                            <div
                                key={faq.id}
                                className={`bg-[#1e2329] border ${expandedId === faq.id ? 'border-blue-500/50' : 'border-gray-800'} rounded-2xl overflow-hidden transition-all duration-300`}
                            >
                                <button
                                    onClick={() => setExpandedId(expandedId === faq.id ? null : faq.id)}
                                    className="w-full text-left px-6 py-5 flex items-center justify-between hover:bg-[#2b3139]/50 transition"
                                >
                                    <span className="font-medium text-white text-lg pr-4">{faq.question}</span>
                                    {expandedId === faq.id ? (
                                        <ChevronUp className="text-blue-500 flex-shrink-0" size={20} />
                                    ) : (
                                        <ChevronDown className="text-gray-500 flex-shrink-0" size={20} />
                                    )}
                                </button>

                                <div
                                    className={`overflow-hidden transition-all duration-300 ${expandedId === faq.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                        }`}
                                >
                                    <div className="px-6 pb-6 text-gray-400 leading-relaxed border-t border-gray-800/50 pt-4">
                                        {faq.answer}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-12">
                            <div className="bg-gray-800/50 inline-flex p-4 rounded-full mb-4">
                                <HelpCircle size={32} className="text-gray-500" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">No results found</h3>
                            <p className="text-gray-400">Try adjusting your search or category filter.</p>
                        </div>
                    )}
                </div>

                <div className="mt-12 text-center bg-[#1e2329] rounded-2xl p-8 border border-gray-800">
                    <h3 className="text-xl font-bold text-white mb-2">Still have questions?</h3>
                    <p className="text-gray-400 mb-6">Can't find the answer you're looking for? Our support team is here to help.</p>
                    <a
                        href="/support"
                        className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-500 transition"
                    >
                        Contact Support
                    </a>
                </div>
            </div>
        </div>
    );
};

export default FAQPage;
